import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../hooks/useApi';
import { formatDateTime } from '../utils/format';
import ErrorState from './ErrorState';

/**
 * <AuditTrail /> — the "what happened, and who did it" feed.
 *
 * Three modes:
 *   • entity: <AuditTrail entityType="job" entityId={7} />       → GET /api/audit
 *   • rule:   <AuditTrail ruleId={3} ruleSource="job" ruleDesc="…" /> → GET /api/audit/rule/:id?source=job|form
 *     (job and form rules share an id space, so always pass ruleSource)
 *   • path:   <AuditTrail path="/api/jobs/7/activity" listKey="activity" />
 *
 * `path` mode is what a job's Activity panel uses: the server merges recorded
 * changes with notes, files, sign-ins, forms and billing docs into one trail, so
 * the panel shows the whole history rather than just the job being created.
 *
 * All three normalise to a common event shape and render a timeline. Automation
 * events that haven't been reverted get a Revert button (POST /api/automation/:runId/revert).
 * Pass `seed={false}` to suppress the sample rows — an audit trail must never show
 * invented entries, so any feed used as a real record should opt out.
 */

const isSystem = (actor) => typeof actor === 'string' && actor.startsWith('SYSTEM');
const actorLabel = (e) => {
  if (e.actor_name) return e.actor_name;
  const actor = e.actor;
  if (!actor) return 'System';
  if (actor.startsWith('SYSTEM')) return 'Automation';
  if (actor.startsWith('USER:')) return 'Team member';
  return actor;
};

// Icon + tint per kind of event, so the feed is scannable at a glance.
const SOURCE_STYLE = {
  job:      { icon: 'fa-briefcase',           bg: '#eff6ff', fg: '#2563eb' },
  audit:    { icon: 'fa-user-pen',            bg: '#eff6ff', fg: '#2563eb' },
  note:     { icon: 'fa-comment-dots',        bg: '#f8fafc', fg: '#475569' },
  file:     { icon: 'fa-paperclip',           bg: '#fffbeb', fg: '#b45309' },
  sign_in:  { icon: 'fa-right-to-bracket',    bg: '#ecfdf5', fg: '#059669' },
  sign_out: { icon: 'fa-right-from-bracket',  bg: '#ecfdf5', fg: '#059669' },
  form:     { icon: 'fa-clipboard-check',     bg: '#f0f9ff', fg: '#0369a1' },
  billing:  { icon: 'fa-file-invoice',        bg: '#fdf4ff', fg: '#a21caf' },
  system:   { icon: 'fa-bolt',                bg: '#f5f3ff', fg: '#8b5cf6' },
};
const styleFor = (e, sys) => (sys ? SOURCE_STYLE.system : (SOURCE_STYLE[e.source] || SOURCE_STYLE.audit));

const has = (v) => v != null && v !== '';

/** The value line: what it went from and to, or just what it was set to. */
const changeLine = (e) => {
  if (has(e.old_value) && has(e.new_value)) return `${e.old_value} → ${e.new_value}`;
  if (has(e.new_value)) return String(e.new_value);
  if (has(e.old_value)) return `was ${e.old_value}`;
  return '';
};

/** Seed used when the live feed is empty/offline, so G3 is always demonstrable. */
const seedEntityEvents = (entityType) => ([
  { id: 's1', actor: 'SYSTEM:3', field: 'Testing status', old_value: 'In progress', new_value: 'Complete',
    reason: 'Mark asset Complete on LOLER pass', run_id: null, reverted_at: null, created_at: null, _rel: '2 hours ago' },
  { id: 's2', actor: 'USER:12', field: 'Status', old_value: 'Scheduled', new_value: 'In progress',
    reason: `Manual update on ${entityType || 'record'}`, run_id: null, reverted_at: null, created_at: null, _rel: 'Yesterday' },
  { id: 's3', actor: 'SYSTEM:1', field: 'Asset', old_value: null, new_value: 'Created from submission',
    reason: 'Create Property Asset from submission', run_id: null, reverted_at: null, created_at: null, _rel: '3 days ago' },
]);

const seedRuleRuns = () => ([
  { run_id: 's-r1', field: 'Testing status', old_value: 'Overdue', new_value: 'Complete',
    reason: 'Condition matched on submit', reverted_at: null, created_at: null, _rel: '2 hours ago', entity_type: 'form_submission', entity_id: 4821 },
  { run_id: 's-r2', field: 'Testing status', old_value: 'Overdue', new_value: 'Complete',
    reason: 'Condition matched on submit', reverted_at: null, created_at: null, _rel: 'Yesterday', entity_type: 'form_submission', entity_id: 4820 },
]);

const AuditTrail = ({ entityType, entityId, ruleId, ruleSource, ruleDesc, path, listKey, seed }) => {
  const ruleMode = ruleId != null;
  const pathMode = !!path;
  const noSeed = seed === false || pathMode;
  const initial = noSeed ? [] : (seed || (ruleMode ? seedRuleRuns() : seedEntityEvents(entityType)));
  const [events, setEvents] = useState(initial);
  const [live, setLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = pathMode
        ? path
        : (ruleMode
          ? `/api/audit/rule/${encodeURIComponent(ruleId)}${ruleSource ? `?source=${encodeURIComponent(ruleSource)}` : ''}`
          : `/api/audit?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`);
      const d = await apiFetch(url);
      const key = listKey || (ruleMode ? 'runs' : 'audit');
      const list = Array.isArray(d) ? d : (d?.[key] || []);
      if (Array.isArray(list) && (list.length || noSeed)) {
        setEvents(list.map((r, i) => ({ ...r, id: r.id ?? r.run_id ?? `e-${i}` })));
        setLive(true);
      }
      setLoadError(null);
    } catch (err) { setLoadError(err); /* keep whatever we have */ }
    setLoading(false);
  }, [pathMode, path, ruleMode, ruleId, ruleSource, entityType, entityId, listKey, noSeed]);

  useEffect(() => { load(); }, [load]);

  const revert = async (e) => {
    const runId = e.run_id;
    // Match by object identity so only the clicked event flips (seed rows may share undefined ids).
    const mark = () => setEvents((prev) => prev.map((x) => (x === e ? { ...x, reverted_at: 'now' } : x)));
    if (!runId || String(runId).startsWith('s')) {
      mark(); // Seed / non-persisted run — reflect optimistically only.
      return;
    }
    setBusy(runId);
    try { await apiFetch(`/api/automation/${runId}/revert`, { method: 'POST' }); } catch { /* offline */ }
    mark();
    setBusy(null);
  };

  if (loading && !events.length) {
    return <div style={{ padding: 20, color: 'var(--cw-text-faint, #6b7280)', fontSize: '0.82rem' }}>Loading activity…</div>;
  }
  // A real record (no seed) that failed to load must say so, not read as empty.
  if (!events.length && loadError && noSeed) {
    return <ErrorState compact thing="activity" error={loadError} onRetry={load} />;
  }
  if (!events.length) {
    return <div style={{ padding: 20, color: 'var(--cw-text-faint, #6b7280)', fontSize: '0.82rem' }}>No recorded activity yet.</div>;
  }

  return (
    <div>
      {!live && !noSeed && (
        <div style={{ fontSize: '0.7rem', color: 'var(--cw-text-faint, #6b7280)', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="fa-solid fa-circle-info" /> Showing sample activity — live entries appear here once recorded.
        </div>
      )}
      <div style={{ position: 'relative' }}>
        {events.map((e, i) => {
          const sys = ruleMode || isSystem(e.actor);
          const reverted = !!e.reverted_at;
          const when = e._rel || (e.created_at ? formatDateTime(e.created_at) : '—');
          const line = changeLine(e);
          const st = styleFor(e, sys);
          const isNote = e.source === 'note';
          return (
            <div key={e.id ?? i} style={{ display: 'flex', gap: 12, paddingBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: st.bg, color: st.fg, fontSize: '0.8rem',
                }}>
                  <i className={`fa-solid ${st.icon}`} />
                </div>
                {i < events.length - 1 && <div style={{ flex: 1, width: 2, background: '#f1f5f9', marginTop: 4 }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>
                    {sys ? 'Automation' : actorLabel(e)}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--cw-text-faint, #6b7280)' }} title={e.created_at || ''}>{when}</span>
                  {reverted && (
                    <span style={{ fontSize: '0.66rem', fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 6, padding: '1px 7px' }}>REVERTED</span>
                  )}
                </div>
                {e.field && (
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    {e.field}
                  </div>
                )}
                {line && (
                  <div style={{
                    fontSize: '0.8rem', color: '#374151', marginTop: 2,
                    ...(isNote ? {
                      whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    } : { wordBreak: 'break-word' }),
                  }}>{line}</div>
                )}
                {e.reason && (
                  <div style={{ fontSize: '0.74rem', color: '#6b7280', marginTop: 3 }}>
                    {sys ? <span style={{ color: 'var(--cw-text-faint, #6b7280)' }}>Why: </span> : null}{e.reason}
                  </div>
                )}
                {/* Only offer a revert where there's a real automation run behind it —
                    a system entry with no run can't be undone. Seeds stay demonstrable. */}
                {sys && !reverted && (e.run_id || !live) && (() => {
                  const isBusy = busy != null && busy === e.run_id;
                  return (
                    <button className="cw-btn cw-btn-secondary" onClick={() => revert(e)} disabled={isBusy}
                      style={{ marginTop: 8, padding: '0.28rem 0.7rem', fontSize: '0.72rem' }}>
                      <i className="fa-solid fa-rotate-left" /> {isBusy ? 'Reverting…' : 'Revert this change'}
                    </button>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
      {ruleDesc && (
        <div style={{ marginTop: 4, paddingTop: 12, borderTop: '1px solid #f3f4f6', fontSize: '0.72rem', color: 'var(--cw-text-faint, #6b7280)' }}>
          Rule: {ruleDesc}
        </div>
      )}
    </div>
  );
};

export default AuditTrail;
